import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  AggregateView,
  FiltersUsed,
  PaymentMethodView,
  RecordView,
  TableView,
} from "../../components/_shared/DetailsPageView";
import {
  resetPayments,
  retrievePaymentDetails,
} from "../../redux/actions/paymentActions";
import { RootState } from "../../redux/rootReducer";
import dateUtils from "../../utils/dateUtils";
import { resetClient } from "../../redux/actions/accountActions";
import ROUTES from "../../routes";
import { Currency } from "../../shared/constant";
import clapUtils, { isClapAdminORClapManager } from "../../utils/clapUtils";
import SpinnerView from "../../components/_shared/SpinnerView";

const PaymentDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const { paymentDetails } = useSelector((state: RootState) => state.payment);
  const [paymentCurrency, setPaymentCurrency] = useState("");
  const { userDetails } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    dispatch(resetClient());
    dispatch(resetPayments());
    dispatch(retrievePaymentDetails({ id: paymentId }));
  }, []);

  useEffect(() => {
    if (paymentDetails) {
      setPaymentCurrency(clapUtils.getCurrencyIcon(paymentDetails.currency));
    }
  }, [paymentDetails]);

  const onInvoiceNoClick = () => {
    navigate(`${ROUTES.PATHS.ADMIN.INVOICES}/${paymentDetails.invoice}`);
  };

  return (
    <SpinnerView isFetching={!paymentDetails}>
      {paymentDetails && (
        <div>
          <div className="flex justify-between">
            <div className="flex items-center gap-4">
              <div className="text-heading20">{paymentDetails.title}</div>
              <div>{clapUtils.getPaymentType(paymentDetails.status)}</div>
            </div>
            {isClapAdminORClapManager(userDetails) && (
              <div
                onClick={onInvoiceNoClick}
                className="text-lg text-dodger-blue underline cursor-pointer"
                aria-hidden="true"
              >
                {paymentDetails.invoice_no}
              </div>
            )}
          </div>
          <div className="mt-6 mb-9 flex justify-between items-center w-full">
            <div className="flex gap-20">
              <div className="max-w-lg">
                <RecordView
                  title="Client:"
                  text={paymentDetails.client_details.name}
                />
                <RecordView
                  title="Payment Created:"
                  text={dateUtils.llMonthFormat(paymentDetails.created_at)}
                />
                <RecordView
                  title="Payment Date:"
                  text={dateUtils.llMonthFormat(paymentDetails.payment_date)}
                />
              </div>
              <div>
                <RecordView
                  title="Invoice Created:"
                  text={
                    paymentDetails.invoice_created === "Not Created"
                      ? paymentDetails.invoice_created
                      : dateUtils.llMonthFormat(paymentDetails.invoice_created)
                  }
                />
                <RecordView
                  title="Exchange Rate Added:"
                  text={
                    paymentDetails.exchange_rate_added_at === "Not Added"
                      ? paymentDetails.exchange_rate_added_at
                      : dateUtils.llMonthFormat(
                          paymentDetails.exchange_rate_added_at,
                        )
                  }
                />
              </div>
            </div>
            <div>
              <div className="text-secondary mb-1.5">
                Amount ({paymentDetails.currency})
              </div>
              <div className="text-heading20 text-right">
                {paymentCurrency} {paymentDetails.amount}
              </div>
            </div>
          </div>
          <div>
            <div className="font-semibold mb-2">Filters Used</div>
            <div className="default-border p-4 space-y-2">
              <FiltersUsed details={paymentDetails} />
            </div>
          </div>
          <div className="mt-4 mb-6">
            {paymentDetails.display_exchange_rate && (
              <TableView
                {...{
                  c1: "",
                  c3: "Exchange Rate",
                  c4: paymentDetails.display_exchange_rate,
                }}
                twClass="mt-[13px]"
              />
            )}
            <AggregateView
              title="Amount in BDT"
              value={`${clapUtils.getCurrencyIcon(Currency.BDT)} ${
                paymentDetails.payable_bdt
              }`}
            />
          </div>
          <div className="mt-12">
            <PaymentMethodView details={paymentDetails} />
          </div>
        </div>
      )}
    </SpinnerView>
  );
};

export default PaymentDetails;
